import React, { useState, useMemo } from 'react'

import {
    Grid,
    Pagination,
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material'
import { CircularProgress, useTheme } from '@material-ui/core'

import {
    Container,
    MessageForm,
    SideDrawer,
    SideDrawerContainer,
} from '../../../components'
import { AddPropertyForm, FormStatus } from '../../../models/Forms'
import { AddPropertyFormRow } from './AddPropertyFormRow'
import { UpdateFormRequest } from '../../../hooks/useAddRemovePropertyForms'
import { normalizeString } from '../../../helpers/stringHelpers'
import { OnboardApartmentModal } from '../../../components/AdminPanel/OnboardApartmentModal'

interface Props {
    addPropertyFormList: AddPropertyForm[]
    loading: boolean
    updateAddPropertyFormState: (updatedValue: AddPropertyForm) => void
    updateAddPropertyForm: (
        request: UpdateFormRequest,
    ) => Promise<AddPropertyForm>
}

export const AddPropertyFormsPage = (props: Props) => {
    const {
        addPropertyFormList,
        loading,
        updateAddPropertyFormState,
        updateAddPropertyForm,
    } = props

    const [page, setPage] = useState(1)
    const itemsPerPage = 10

    const [openMessageDrawer, setOpenMessageDrawer] = useState(false)
    const [selectedForm, setSelectedForm] = useState<AddPropertyForm | null>(
        null,
    )
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<FormStatus | 'ALL'>(
        FormStatus.PENDING,
    )

    const [onboardModalOpen, setOnboardModalOpen] = useState(false)

    const theme = useTheme()

    // Filter forms based on search query and status, then sort by most recent (modified_date desc)
    const filteredForms = useMemo(() => {
        const filtered = addPropertyFormList.filter((form) => {
            const query = searchQuery.toLowerCase()
            const matchesSearch =
                form.property_name.toLowerCase().includes(query) ||
                form.organization.name.toLowerCase().includes(query) ||
                (form.notes?.toLowerCase().includes(query) ?? false) ||
                (form.billing_email?.toLowerCase().includes(query) ?? false)
            const matchesStatus =
                statusFilter === 'ALL' || form.status === statusFilter
            return matchesSearch && matchesStatus
        })
        return [...filtered].sort((a, b) => {
            const dateA = new Date(a.modified_date ?? a.created_date).getTime()
            const dateB = new Date(b.modified_date ?? b.created_date).getTime()
            return dateB - dateA
        })
    }, [addPropertyFormList, searchQuery, statusFilter])

    // Update pagination calculations to use filtered forms
    const totalPages = Math.ceil(filteredForms.length / itemsPerPage)
    const startIndex = (page - 1) * itemsPerPage
    const paginatedForms = filteredForms.slice(
        startIndex,
        startIndex + itemsPerPage,
    )

    const handlePageChange = (
        event: React.ChangeEvent<unknown>,
        value: number,
    ) => {
        setPage(value)
    }

    const handleOpenMessageDrawer = (form: AddPropertyForm) => {
        setSelectedForm(form)
        setOpenMessageDrawer(true)
    }

    const handleUpdateForm = async (request: UpdateFormRequest) => {
        await updateAddPropertyForm(request)
    }

    return (
        <SideDrawerContainer open={openMessageDrawer} width={DRAWER_W}>
            <Container
                style={{
                    padding: theme.spacing(0, 1),
                    height: '100%',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    width: '100%',
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search by property or organization name..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value)
                            setPage(1)
                        }}
                        style={{ minWidth: '400px' }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => {
                                setStatusFilter(
                                    e.target.value as FormStatus | 'ALL',
                                )
                                setPage(1)
                            }}
                        >
                            <MenuItem value="ALL">All Statuses</MenuItem>
                            {Object.values(FormStatus).map((status) => (
                                <MenuItem key={status} value={status}>
                                    {normalizeString(status)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Container
                    style={{
                        flexDirection: 'column',
                        overflowY: 'auto',
                        marginBottom: theme.spacing(1),
                        maxHeight: `calc(100vh - 280px)`,
                        minHeight: `calc(100vh - 280px)`,
                        flexGrow: 1,
                    }}
                >
                    {loading ? (
                        <Container
                            style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <CircularProgress size={50} />
                        </Container>
                    ) : (
                        <Grid container spacing={0}>
                            {paginatedForms.map((form, index) => (
                                <Grid item xs={12} key={index}>
                                    <AddPropertyFormRow
                                        key={index}
                                        form={form}
                                        theme={theme}
                                        handleOpenMessageDrawer={
                                            handleOpenMessageDrawer
                                        }
                                        handleUpdateForm={handleUpdateForm}
                                        onOpenOnboardModal={() =>
                                            setOnboardModalOpen(true)
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Container>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 2,
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'background.default',
                    }}
                >
                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    )}
                </Box>
            </Container>
            <SideDrawer
                open={selectedForm !== null && openMessageDrawer}
                width={DRAWER_W}
                handleClose={() => {
                    setSelectedForm(null)
                    setOpenMessageDrawer(false)
                }}
            >
                <MessageForm
                    theme={theme}
                    channelId={selectedForm?.channel}
                    afterCreateMessage={() => {
                        if (selectedForm) {
                            updateAddPropertyFormState({
                                ...selectedForm,
                                message_count:
                                    (selectedForm.message_count ?? 0) + 1,
                            })
                        }
                    }}
                />
            </SideDrawer>
            <OnboardApartmentModal
                open={onboardModalOpen}
                onClose={() => setOnboardModalOpen(false)}
            />
        </SideDrawerContainer>
    )
}

const DRAWER_W = 420
